import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {NgForm} from '@angular/forms';
import {RegisterCompanyStepBase} from '../register-company-step.base';

export type RegisterCompanyDocumentField = 'id_front_file' | 'id_back_file' | 'cf_front_file' | 'cf_back_file';

@Component({
    selector: 'app-register-company-legal-data',
    templateUrl: './legal-data-step.component.html',
    styleUrls: ['../../register-company.component.scss']
})
export class RegisterCompanyLegalDataStepComponent extends RegisterCompanyStepBase implements OnDestroy {

    @Input() id_front_file: File | null = null;
    @Input() id_back_file: File | null = null;
    @Input() cf_front_file: File | null = null;
    @Input() cf_back_file: File | null = null;
    @Input() uploadingFields: Partial<Record<RegisterCompanyDocumentField, boolean>> = {};
    @Input() uploadErrors: Partial<Record<RegisterCompanyDocumentField, string>> = {};

    @Output() documentChosen = new EventEmitter<{ event: Event; field: RegisterCompanyDocumentField }>();
    @Output() documentDropped = new EventEmitter<{ event: DragEvent; field: RegisterCompanyDocumentField }>();

    submitted = false;
    dragOverField: RegisterCompanyDocumentField | null = null;
    fileErrors: Partial<Record<RegisterCompanyDocumentField, string>> = {};
    localPreviewUrls: Partial<Record<RegisterCompanyDocumentField, string>> = {};

    readonly identityDocuments: Array<{
        field: RegisterCompanyDocumentField;
        label: string;
        hint: string;
    }> = [
        {field: 'id_front_file', label: "Carta d'identità — fronte", hint: 'Foto o scansione del fronte'},
        {field: 'id_back_file', label: "Carta d'identità — retro", hint: 'Foto o scansione del retro'},
    ];

    readonly fiscalDocuments: Array<{
        field: RegisterCompanyDocumentField;
        label: string;
        hint: string;
    }> = [
        {field: 'cf_front_file', label: 'Codice fiscale — fronte', hint: 'Tessera o documento fronte'},
        {field: 'cf_back_file', label: 'Codice fiscale — retro', hint: 'Tessera o documento retro'},
    ];

    get fiscalCodeLength(): number {
        return (this.company.legalFiscalCode || '').replace(/\s+/g, '').length;
    }

    get allDocumentsUploaded(): boolean {
        return this.identityDocuments.concat(this.fiscalDocuments).every(doc => this.hasDocument(doc.field));
    }

    get hasPendingUploads(): boolean {
        return Object.values(this.uploadingFields).some(Boolean);
    }

    getFile(field: RegisterCompanyDocumentField): File | null {
        return this[field];
    }

    getUploadedUrl(field: RegisterCompanyDocumentField): string {
        const urls: Record<RegisterCompanyDocumentField, string | undefined> = {
            id_front_file: this.company.legalDocumentFront,
            id_back_file: this.company.legalDocumentBack,
            cf_front_file: this.company.legalFiscalCodeFront,
            cf_back_file: this.company.legalFiscalCodeBack,
        };
        return urls[field] ?? '';
    }

    getPreviewUrl(field: RegisterCompanyDocumentField): string {
        return this.localPreviewUrls[field] ?? this.getUploadedUrl(field);
    }

    hasDocument(field: RegisterCompanyDocumentField): boolean {
        return !!(this.getFile(field) || this.getUploadedUrl(field));
    }

    isUploading(field: RegisterCompanyDocumentField): boolean {
        return !!this.uploadingFields[field];
    }

    getError(field: RegisterCompanyDocumentField): string {
        return this.fileErrors[field] ?? this.uploadErrors[field] ?? '';
    }

    isPdf(field: RegisterCompanyDocumentField): boolean {
        const file = this.getFile(field);
        if (file) return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        return this.getUploadedUrl(field).split('?')[0].toLowerCase().endsWith('.pdf');
    }

    getDocumentName(field: RegisterCompanyDocumentField): string {
        const file = this.getFile(field);
        if (file) return file.name;
        const url = this.getUploadedUrl(field);
        if (!url) return '';
        try {
            return decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'Documento caricato');
        } catch {
            return 'Documento caricato';
        }
    }

    dropzoneClasses(field: RegisterCompanyDocumentField): string {
        const base = '!m-0 !flex !w-full !min-h-[8.5rem] !flex-col !items-center !justify-center !gap-1.5 !appearance-none !rounded-2xl !border-[1.5px] !border-dashed !px-4 !py-5 !text-center !font-[inherit] !text-inherit !cursor-pointer !transition focus-visible:!outline-none focus-visible:!ring-2 focus-visible:!ring-primary';
        const hasError = !!this.getError(field) || (this.submitted && !this.hasDocument(field));

        if (hasError) {
            return `${base} !border-red-300 !bg-red-50`;
        }
        if (this.hasDocument(field)) {
            return `${base} !border-solid !border-[rgba(var(--primary-rgb),0.45)] !bg-primary-soft`;
        }
        if (this.dragOverField === field) {
            return `${base} !border-primary !bg-primary-soft`;
        }
        return `${base} !border-slate-300 !bg-slate-50 hover:!border-primary hover:!bg-primary-soft`;
    }

    normalizeFiscalCode(): void {
        if (typeof this.company.legalFiscalCode === 'string') {
            this.company.legalFiscalCode = this.company.legalFiscalCode.replace(/\s+/g, '').toUpperCase();
        }
        this.persistForm();
    }

    normalizeDocumentNumber(): void {
        if (typeof this.company.legalDocumentNumber === 'string') {
            this.company.legalDocumentNumber = this.company.legalDocumentNumber.replace(/\s+/g, '').toUpperCase();
        }
        this.persistForm();
    }

    onDragOver(event: DragEvent, field: RegisterCompanyDocumentField): void {
        event.preventDefault();
        this.dragOverField = field;
    }

    onDragLeave(field: RegisterCompanyDocumentField): void {
        if (this.dragOverField === field) {
            this.dragOverField = null;
        }
    }

    chooseDocument(event: Event, field: RegisterCompanyDocumentField): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (!this.acceptFile(file, field)) {
            input.value = '';
            return;
        }
        this.setLocalPreview(field, file);
        this.documentChosen.emit({event, field});
    }

    onDocumentDrop(event: DragEvent, field: RegisterCompanyDocumentField): void {
        event.preventDefault();
        this.dragOverField = null;
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;
        if (!this.acceptFile(file, field)) return;
        this.setLocalPreview(field, file);
        this.documentDropped.emit({event, field});
    }

    tryContinue(form: NgForm): void {
        this.submitted = true;
        form.form.markAllAsTouched();
        if (form.invalid || !this.allDocumentsUploaded || this.hasPendingUploads) {
            return;
        }
        this.nextStep();
    }

    ngOnDestroy(): void {
        Object.values(this.localPreviewUrls).forEach(url => {
            if (url) URL.revokeObjectURL(url);
        });
    }

    private setLocalPreview(field: RegisterCompanyDocumentField, file: File): void {
        const previousUrl = this.localPreviewUrls[field];
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        this.localPreviewUrls[field] = URL.createObjectURL(file);
    }

    private acceptFile(file: File, field: RegisterCompanyDocumentField): boolean {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        const allowedExt = ['pdf', 'jpg', 'jpeg', 'png'];

        if (!allowedTypes.includes(file.type) && !allowedExt.includes(ext)) {
            this.fileErrors[field] = 'Formato non valido. Usa JPG, PNG o PDF.';
            return false;
        }

        const maxBytes = 10 * 1024 * 1024;
        if (file.size > maxBytes) {
            this.fileErrors[field] = 'Il file supera i 10 MB.';
            return false;
        }

        delete this.fileErrors[field];
        return true;
    }
}
